import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import clone from 'lodash/cloneDeep';
import {
    WS_EVENT_UPDATE_ACTION,
    WS_EVENT_INPLAY_UPDATE_ACTION,
} from '~shared/services/mainSocketClass';

import { getJson } from '../../../shared/utils/http';
import { INPLAY_SPORT_PARAM } from '../../inplay/constants';
import { IPW_API_URL, IPW_WS_HANDLER_COMPONENT_NAME } from './constants';
import mainSocket from '../inplaySocket';
import InplayWidget from './InplayWidget';
import { buildSpectateUrl } from '~spa/Utils/Spectate';
import { withLazyLoadContext } from '~spa/Components/LazyLoad/LazyLoad';
import { mergeMarkets, getMarketBetable } from '~shared/utils/market-utils';
import { selectionArrayToObject, updateSelection } from '~shared/utils/selections-utils';
import isArray from 'lodash/isArray';
import {
    eventIsOver,
} from '~shared/components/sport-event/utils';

class InplayWidgetContainer extends React.Component {

    static propTypes = {
        sport: PropTypes.string,
        isShowFooter: PropTypes.bool,
        source: PropTypes.string,
        lazyLoadHasLoaded: PropTypes.func,
        lazyLoadIsEmpty: PropTypes.func,
    };

    static supportedWsActions = [WS_EVENT_INPLAY_UPDATE_ACTION, WS_EVENT_UPDATE_ACTION];

    static defaultProps = {
        sport: null,
        isShowFooter: false,
        lazyLoadHasLoaded: () => null,
        lazyLoadIsEmpty: () => null,
    };

    state = {
        marketsSuspended: {},
        eventsEnded: {},
        unsubscribeForUpdatesFn: false,
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !isEqual(nextProps, this.props) || !isEqual(nextState, this.state);
    }

    componentDidMount() {
        this.fetchEvents();
    }

    componentWillUnmount() {
       this._unsubscribeToWsUpdates();
    }

    async fetchEvents() {
        const { sport, source } = this.props;
        const sportFilter = sport ? `${INPLAY_SPORT_PARAM}=${sport}`: '';
        const filterFeatured = `${IPW_API_URL}?${sportFilter}`;
        const response = await getJson(buildSpectateUrl(filterFeatured));
        const { selections, events = [], markets, markets_headers } = await response.json();

        if (events.length) {
            const eventsEnded = {};
            for (const event of events) {
                eventsEnded[event.id] = false;
            }
            await new Promise((resolve) => {
                super.setState({
                    selections,
                    events,
                    markets,
                    isShowFooter: this.props.isShowFooter,
                    markets_headers,
                    eventsEnded,
                    source,
                    unsubscribeForUpdatesFn: this._subscribeToWsUpdates(events),
                }, resolve);
            });
            this.props.lazyLoadHasLoaded();
        } else {
            this.props.lazyLoadIsEmpty();
        }
    }

    /**
     * get an array of objects hydrated by event_id (which is the key of the input object)
     *
     * @param {Array} events Array of event objects received from API
     * @returns {Object} payload of {event_id: 1, markets: [{id: 1}, {id: 2}]}
     * @memberof InplayWidgetContainer
     */
     _getParamsToWebSocket(events = []) {
        const requestPackets = events.map(({id:event_id, market_id, grouped_market_ids: groupedMarkets = []}) => {
            const requestPacket = groupedMarkets.length
                ? groupedMarkets.map(market_id => ({
                        event_id,
                        market_id,
                }))
                : { event_id, market_id };

            return requestPacket;
        });

        return [].concat(...requestPackets);
    }
    /**
     * run when web socket payload received to update InplayWidget
     *
     * @param {Object} event the event received from the web socket
     * @param {Object} state State object
     * @returns {void}
     * @memberof InplayWidgetContainer
     */
    _getUpdates(event, state) {
        const {
            id: eventId,
            markets: updatedMarkets = [],
        } = event;
        const statedEvent = state.events.find(({ id }) => event.id === id) || {};
        const { usesThreeMarketView = false, grouped_market_ids: marketIds = []} = statedEvent;
        const { selections: _currentSelections = {}, markets: _currentMarkets = {} } = state;
        if (usesThreeMarketView) {
            const currentEventSelections = clone(_currentSelections[eventId]);
            const currentEventMarkets = _currentMarkets[eventId];

            const mergedMarkets = mergeMarkets(updatedMarkets);
            const marketUpdates = marketIds.map(marketId => mergedMarkets.find(market => market.id === marketId)).filter(Boolean);
            marketUpdates.forEach(_updatedMarket => {
                const {
                    id: marketId,
                    selections: _updatedSelections,
                    default_line_selections: updatedDefaultLineSelections = [],
                    ...updatedMarket
                } = _updatedMarket;
                const currentSelections = selectionArrayToObject(currentEventSelections[marketId]) || {};
                const { default_line_selections: currentDefaultLineSelections = [] } = currentEventMarkets[marketId] || {};

                const shouldUpdateDefaultLine = Boolean(updatedDefaultLineSelections.length);
                const default_line_selections = shouldUpdateDefaultLine
                                                ? updatedDefaultLineSelections
                                                : currentDefaultLineSelections;

                // inplay updates are formatted differently from price updates.
                const updatedSelections = isArray(_updatedSelections)
                                        ? selectionArrayToObject(_updatedSelections)
                                        : _updatedSelections;

                const isMarketBetable = getMarketBetable(updatedMarket);

                currentEventSelections[marketId] = default_line_selections.map(selectionId => {
                    const currentSelection = currentSelections[selectionId] || {};
                    const updatedSelection = updatedSelections[selectionId] || false;
                    if (!updatedSelection) {
                        return currentSelection;
                    }

                    return updateSelection(currentSelection, {id: selectionId, ...updatedSelection }, isMarketBetable);
                });
                currentEventMarkets[marketId] = {
                    id: marketId,
                    ...updatedMarket,
                    default_line_selections,
                };
            });

            return {
                selections: { ..._currentSelections, [eventId]: currentEventSelections },
                markets: { ..._currentMarkets, [eventId]: currentEventMarkets },
            };
        }

        const oldSelections = state.selections;
        let newSelections = null;
        for (const market of event.markets) {
            if (statedEvent && market.id === statedEvent.market_id && market.selections) {
                if (Array.isArray(market.selections)) {
                    newSelections = market.selections.reduce((acc, selection) => ({
                        ...acc,
                        [selection.id]: selection,
                    }), {});
                } else {
                    newSelections = market.selections;
                }
                break;
            }
        }

        const addNewSelectionsToItems = () =>
            oldSelections[event.id].map(item => {
                if (!item) {
                    const validIds = oldSelections[event.id]
                        .filter(Boolean)
                        .map(({id}) => id);

                    const id = Object.keys(newSelections).find(key => {
                        return !validIds.includes(key);
                    });

                    return newSelections[id];
                }

                return { ...item, ...(newSelections && { ...newSelections[item.id] }) };
            });

        return {
            selections: {
                ...this.state.selections,
                // check if selections[event.id] exits and then call addNewSelectionsToItems
                ...(this.state.selections[event.id] && { [event.id]: addNewSelectionsToItems() }),
            },
        };
    }

    /**
     * checks if the given event is suspended
     * @param {Object} newEvent new event to check
     * @param {Object} event event in state
     * @param {Object} state state
     * @returns {Boolean} check if newEvent is suspended
     * @memberof InplayWidgetContainer
     */
    _isEventSuspended(newEvent, event, state) {
        const allMarketsSuspended = Object.values(this.state.markets[event.id]).every(({suspended = true}) => suspended);
        const eventEnded = state.eventsEnded[event.id];
        const eventSuspended = newEvent.suspended === undefined ? event.suspended : newEvent.suspended;

        return allMarketsSuspended || eventSuspended || eventEnded;
    }

    /**
     * finds matching events, and sets suspended flag if found
     * @param {Object} newEvent new event received from Ws
     * @param {Object} state A state object
     * @returns {Array} array of new events
     * @memberof InplayWidgetContainer
     */
    _getEventsWithSuspendedFlag(newEvent, state) {
        return state.events.map(event => {
            return {
                ...event,
                ...(
                    event.id === newEvent.id &&
                    {
                        suspended: this._isEventSuspended(
                            newEvent,
                            event,
                            state,
                        ),
                    }
                ),
            };
        });
    }

    /**
     * Stores market's suspended flag into the state
     *
     * @param {Object} event Event coming from ws update message
     * @returns {void}
     */
    _storeMarketSuspended(event) {
        const eventId = event.id;
        const { selections: stateSelections, marketsSuspended } = this.state;
        if (stateSelections[eventId]) {
            const marketSuspended = marketsSuspended[eventId];
            for (const market of event.markets) {
                if (market.id in marketSuspended) {
                    this.setState({
                        marketsSuspended: {
                            ...marketsSuspended,
                            [eventId]: { ...marketsSuspended[eventId], [market.id]: market.suspended },
                        },
                    });
                }
            }
        }
    }

    /**
     * when an inplay event ended, delete it from state
     *
     * @param {Object} newEvent Event coming from ws update message
     * @param {Object} state A state object
     * @returns {Array} new state without the deleted event
     */
     _removeEndedEvent (newEvent, state) {
        return state.events.filter(event => event.id !== newEvent.id);
     }

    /**
     * Runs when web socket update is received
     *
     * @param {Object} data data from web socket
     * @returns {void}
     * @memberof InplayWidgetContainer
     */
    _onWsEventUpdate(data) {
        const action = data.action;
        const { events } = this.state;
        let event = {};
        if ('event' in data) {
            event = data.event;
        }

        if ('events' in data) {
            [ event ] = data.events;
        }

        const shouldUpdateMarket = Array.isArray(event.markets) && event.markets.length;
        const shouldUpdatePrices =
            (action === WS_EVENT_UPDATE_ACTION
            || action === WS_EVENT_INPLAY_UPDATE_ACTION)
            && shouldUpdateMarket;

        const isEventEnded = eventIsOver(event.match_status);

        if (isEventEnded) {
            const eventToUnsubscribe = events.find(item => item.id === event.id);

            this._unsubscribeToWsUpdates([eventToUnsubscribe]);
            this.setState(state => ({
                events: this._removeEndedEvent(event, state),
            }));
        } else {
            this.setState(state => ({
                events: this._getEventsWithSuspendedFlag(event, state),
                ...(shouldUpdatePrices && { ...this._getUpdates(event, state) }),
            }));
        }
    }

    /**
     * use selections from original get request to subscribe to sockets
     *
     * @param {Array} events Array of event objects received from API
     * @returns {void}
     * @memberof InplayWidgetContainer
     */
    _subscribeToWsUpdates(events) {
        const params = this._getParamsToWebSocket(events);
        mainSocket.subscribeForUpdates(params, IPW_WS_HANDLER_COMPONENT_NAME);
        mainSocket.onWsEventUpdate(data => {
            if (InplayWidgetContainer.supportedWsActions.includes(data.action)) {
                this._onWsEventUpdate(data);
            }
        });

        return (requestPacket) => mainSocket.unsubscribeForUpdates(requestPacket);
    }

    /**
     * handle ws unregister, if events is empty, then unregister all current events,
     * otherwise unregister the target event
     *
     * @param {Array} events Array of event objects received from state or _onWsEventUpdate
     * @returns {void}
     * @memberof InplayWidgetContainer
     */
    _unsubscribeToWsUpdates (events = []) {
        let eventsToUnregister;
        if (!events.length) {
            eventsToUnregister = this.state.events;
            mainSocket.removeWsEventHandlers(IPW_WS_HANDLER_COMPONENT_NAME);
        } else {
            eventsToUnregister = events;
        }
        const requestPackets = this._getParamsToWebSocket(eventsToUnregister);
        if(this.state.unsubscribeForUpdatesFn) {
            this.state.unsubscribeForUpdatesFn(requestPackets);
        }
    }

    render() {
        if (!isEmpty(this.state.selections)) {
            return <InplayWidget { ...this.state } />;
        }

        return null;
    }
}

export default withLazyLoadContext()(InplayWidgetContainer);
